import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { Loader2, X, Eye, EyeOff, Check } from "lucide-react";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";
import { Progress } from "@/components/ui/progress";

export const Route = createFileRoute("/sign-up")({
  component: SignUp,
});

const isValidEmail = (email: string): boolean => {
  const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailPattern.test(email);
};

const isValidPassword = (password: string): boolean => {
  const minLength = password.length >= 8;
  const hasNumber = /\d/.test(password);
  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  return minLength && hasNumber && hasSymbol;
};

const isValidUsername = (username: string): boolean => {
  // lowercase letters, numbers, underscore; 3-30 chars
  return /^[a-z0-9_]{3,30}$/.test(username);
};

const doPasswordsMatch = (
  password: string,
  confirmPassword: string
): boolean => {
  return password === confirmPassword;
};

function SignUp() {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirmation, setPasswordConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const router = useRouter();
  const passLen = password.length;
  const reqLengthOk = passLen >= 8;
  const reqExtraLength = passLen >= 12;
  const reqNumber = /\d/.test(password);
  const reqSymbol = /[^A-Za-z0-9]/.test(password);
  const reqMixedCase = /[a-z]/.test(password) && /[A-Z]/.test(password);
  const strengthScore =
    Number(reqLengthOk) +
    Number(reqExtraLength) +
    Number(reqNumber) +
    Number(reqSymbol) +
    Number(reqMixedCase);
  const strengthValue = Math.round((strengthScore / 5) * 100);
  const strengthLabel = strengthScore < 2 ? "weak" : strengthScore < 4 ? "okay" : "strong";
  const strengthColor = strengthScore < 2 ? "text-red-500" : strengthScore < 4 ? "text-amber-500" : "text-green-600";
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="flex min-h-svh items-center justify-center p-4">
      <div className="flex w-full flex-col items-center gap-6">
        <img
          src="/header-logo.png"
          alt="clove.moe"
          className="h-12 w-auto align-middle"
        />
        <Card className="z-50 w-full max-w-md rounded-md shadow-lg bg-white">
          <CardHeader>
            <CardTitle className="text-lg md:text-xl text-[#353535]">sign up</CardTitle>
            <CardDescription className="text-xs md:text-sm text-accent">
              enter your information to create an account
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="username" className="text-[#353535]">
                  username
                </Label>
                <Input
                  id="username"
                  placeholder="yourname"
                  required
                  onChange={(e) => {
                    const v = e.target.value;
                    setUsername(v);
                    if (usernameError && v.trim().length > 0) setUsernameError(null);
                  }}
                  value={username}
                  aria-invalid={Boolean(usernameError) || undefined}
                  aria-describedby={usernameError ? "username-error" : undefined}
                  className={`${usernameError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"}`}
                />
                {usernameError && (
                  <p id="username-error" className="text-xs text-red-600 mt-1">{usernameError}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email" className="text-[#353535]">email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="me@example.com"
                  required
                  onChange={(e) => {
                    const v = e.target.value;
                    setEmail(v);
                    if (emailError && v.trim().length > 0) setEmailError(null);
                  }}
                  value={email}
                  aria-invalid={Boolean(emailError) || undefined}
                  aria-describedby={emailError ? "email-error" : undefined}
                  className={`${emailError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"}`}
                />
                {emailError && (
                  <p id="email-error" className="text-xs text-red-600 mt-1">{emailError}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password" className="text-[#353535]">password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPassword(v);
                      if (passwordError && v.trim().length > 0) setPasswordError(null);
                    }}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => setPasswordFocused(false)}
                    autoComplete="new-password"
                    placeholder="password"
                    aria-invalid={Boolean(passwordError) || undefined}
                    aria-describedby={passwordError ? "password-error" : undefined}
                    className={`${passwordError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-3 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {passwordError && (
                  <p id="password-error" className="text-xs text-red-600 mt-1">{passwordError}</p>
                )}
                {passwordFocused && (
                  <div className="mt-2 space-y-2">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-[#353535]">strength</span>
                      <div className="flex-1">
                        <Progress value={strengthValue} className="h-1.5" />
                      </div>
                      <span className={`text-xs font-medium ${strengthColor}`}>{strengthLabel}</span>
                    </div>
                    <ul className="grid grid-cols-1 gap-1">
                      <li className="flex items-center gap-2 text-xs text-[#353535]">
                        {reqLengthOk ? (
                          <Check size={14} className="text-accent" />
                        ) : (
                          <span className="h-3 w-3 rounded-full border border-gray-300" />
                        )}
                        <span>at least 8 characters</span>
                      </li>
                      <li className="flex items-center gap-2 text-xs text-[#353535]">
                        {reqNumber ? (
                          <Check size={14} className="text-accent" />
                        ) : (
                          <span className="h-3 w-3 rounded-full border border-gray-300" />
                        )}
                        <span>includes a number</span>
                      </li>
                      <li className="flex items-center gap-2 text-xs text-[#353535]">
                        {reqSymbol ? (
                          <Check size={14} className="text-accent" />
                        ) : (
                          <span className="h-3 w-3 rounded-full border border-gray-300" />
                        )}
                        <span>includes a symbol</span>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password_confirmation" className="text-[#353535]">confirm password</Label>
                <div className="relative">
                  <Input
                    id="password_confirmation"
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordConfirmation}
                    onChange={(e) => {
                      const v = e.target.value;
                      setPasswordConfirmation(v);
                      if (confirmError && v.trim().length > 0) setConfirmError(null);
                    }}
                    autoComplete="new-password"
                    placeholder="confirm password"
                    aria-invalid={Boolean(confirmError) || undefined}
                    aria-describedby={confirmError ? "confirm-error" : undefined}
                    className={`${confirmError ? "border-red-500 focus-visible:ring-red-500" : "focus-visible:ring-accent"} pr-10`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-3 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </button>
                </div>
                {confirmError && (
                  <p id="confirm-error" className="text-xs text-red-600 mt-1">{confirmError}</p>
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="image" className="text-[#353535]">profile image (optional)</Label>
                <div className="flex items-end gap-4 flex-wrap">
                  {imagePreview && (
                    <div className="relative w-16 h-16 rounded-sm overflow-hidden">
                      <img
                        src={imagePreview}
                        alt="profile preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex items-center gap-2 w-full min-w-0">
                    <Input
                      id="image"
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      className="w-full min-w-0 focus-visible:ring-accent"
                    />
                    {imagePreview && (
                      <X
                        className="cursor-pointer text-accent hover:text-accent"
                        onClick={() => {
                          setImage(null);
                          setImagePreview(null);
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-accent text-white hover:opacity-90"
                disabled={loading}
                onClick={async () => {
                  const normalizedUsername = username.trim().toLowerCase();
                  let hasError = false;

                  if (!isValidUsername(normalizedUsername)) {
                    setUsernameError("username must be 3-30 characters, lowercase letters, numbers, or _");
                    hasError = true;
                  }
                  if (!isValidEmail(email)) {
                    setEmailError("please enter a valid email address");
                    hasError = true;
                  }

                  if (!isValidPassword(password)) {
                    setPasswordError("password must be at least 8 characters and include a number and a symbol");
                    hasError = true;
                  }

                  if (!doPasswordsMatch(password, passwordConfirmation)) {
                    setConfirmError("passwords do not match");
                    hasError = true;
                  }

                  if (hasError) return;

                  await authClient.signUp.email(
                    {
                      email,
                      password,
                      username: normalizedUsername,
                      name: normalizedUsername,
                      image: image ? await convertImageToBase64(image) : "",
                    },
                    {
                      onRequest: () => setLoading(true),
                      onResponse: () => setLoading(false),
                      // errors are handled globally by auth client onError
                      onSuccess: async () => {
                        toast.success("check your email to verify your account");
                        router.navigate({ to: "/sign-in" });
                      },
                    }
                  );
                }}
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  "create an account"
                )}
              </Button>
            </div>
          </CardContent>
          <CardFooter>
            <div className="flex justify-center w-full border-t pt-4">
              <div className="flex items-center gap-2">
                <a
                  href="/sign-in"
                  className="ml-auto inline-block text-sm text-accent hover:text-accent underline cursor-pointer"
                >
                  already have an account?
                </a>
              </div>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}

async function convertImageToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
